<script setup>
/**
 * The commit point. Docked, permanent, and it says what state the page is in.
 *
 * THIS IS THE ONE COMPLAINT BOTH BLIND CRITICS MADE INDEPENDENTLY, in light and in dark:
 *
 *   "nothing on screen confirms the work is saved — the explainer promises 'changes take
 *    effect on the very next message' but there is no save state, timestamp or button
 *    anywhere in view."
 *   "The page has no commit point … there is no save button, save state, or 'saved'
 *    confirmation anywhere in view. Anchor a persistent save/status bar."
 *
 * Both were right, and the old build was worse than they could see: the Save button
 * existed, but at the very bottom of a 2,000px page, so on any screen it was below the
 * fold from the moment the page opened. A commit affordance you have to scroll to find is
 * not a commit affordance. This bar renders OUTSIDE the scrolling `main` (see the
 * `footer` slot in `WasloPageLayout`), so it is on screen at every scroll position, in
 * every state, always.
 *
 * FIVE STATES, and the copy for each is the caller's, because only the caller knows WHAT
 * changed:
 *   idle    — loaded, nothing touched. "Everything is saved."
 *   dirty   — edits pending, named. Not "unsaved changes" but WHICH ones.
 *   saving  — in flight.
 *   saved   — with the section names and the wall-clock time it happened.
 *   error   — the save was refused, and the draft is still on screen.
 *
 * The toast that used to be the only confirmation stays, but it is no longer the only
 * one: a toast is gone in four seconds and answers "did that work" for exactly as long.
 */
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { computed } from 'vue';

const props = defineProps({
  state: {
    type: String,
    default: 'idle',
    // Inline rather than a module constant: `defineProps` is hoisted out of `setup()`, so
    // it cannot reference anything declared beside it.
    validator: value =>
      ['idle', 'dirty', 'saving', 'saved', 'error'].includes(value),
  },
  /** The headline. One short sentence naming the state. */
  status: { type: String, default: '' },
  /** What changed, or what was saved and when. Empty is fine. */
  detail: { type: String, default: '' },
  saveLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  /**
   * A SECOND FACT, REPORTED BESIDE THE SAVE STATE AND NEVER DERIVED FROM IT.
   *
   * A blind reader looked at the task composer's bar and read "Everything is saved — your
   * assistant is using this now" while standing on screen 1 of 5. Both halves of that were
   * being carried by one line, and only the first half was the bar's business: whether the
   * work is SAVED and whether the thing is SERVING CUSTOMERS are two different questions,
   * and the second one has to be answerable in every save state — including `error`, where
   * the detail line is about the refusal, and `dirty`, where it is about what is pending.
   * So it cannot live in `detail`, which changes with the state; it is its own element.
   *
   * Empty renders nothing, which is every other caller: a page whose subject has no
   * on/off of its own has no second fact to report, and inventing one would be worse than
   * silence. `WASLO.TASKS.COMPOSE.LIVE.*` is the only caller today.
   */
  note: { type: String, default: '' },
  noteTone: {
    type: String,
    default: 'off',
    validator: value => ['on', 'off'].includes(value),
  },
});

const emit = defineEmits(['save']);

// Emerald for a confirmed write (`n-blue-*` IS the accent — the overlay remaps that scale,
// and `n-brand` would render Chatwoot blue, §5 R3). Ruby for a refusal. Slate for the two
// states that are merely true.
const iconClass = computed(
  () =>
    ({
      idle: 'i-lucide-check text-n-slate-10',
      dirty: 'i-lucide-circle-dashed text-n-slate-11',
      saved: 'i-lucide-circle-check text-n-blue-10',
      error: 'i-lucide-triangle-alert text-n-ruby-9',
    })[props.state] ?? 'i-lucide-check text-n-slate-10'
);

const statusClass = computed(() =>
  props.state === 'error' ? 'text-n-ruby-9' : 'text-n-slate-12'
);

// ── the idle button, and why it is not a greyed-out primary ─────────────────────────
// Both blind critics named this button, independently, in opposite themes, in the round
// that introduced it: "a muted green fill with slightly lighter green text at roughly 2:1
// contrast; in its disabled state it reads as an unfinished or half-rendered button",
// "light grey text on a light grey fill at roughly 2:1 contrast, which reads as broken
// rather than deliberately idle". They measured the same number from two different
// backgrounds, which is the tell that it is arithmetic and not taste.
//
// It is arithmetic. `Button.vue`'s base carries `disabled:opacity-50`, and opacity
// composites the WHOLE element over the page. A solid accent fill with `text-white` is the
// worst possible input to that: the white label blends toward the page, the emerald fill
// blends toward the page, and the two meet in the middle. White-on-emerald starts at ~5:1
// and lands at ~2:1 in BOTH themes — which is exactly why one critic saw it in light and
// one in dark and both reported the same ratio. The previous round declined to restyle it
// because Chatwoot's own disabled primary looks like this; matching a defect is not a
// defence, and "the framework does it too" is not something an owner can read.
//
// The fix is to stop asking a primary to represent "nothing to do". A control's emphasis
// should track whether it has anything to act on:
//   • dirty / saving / error → there IS something to commit. Solid accent, full contrast,
//     enabled (error included — the draft is still on screen and the button is the retry).
//   • idle / saved           → there is nothing to commit. A low-emphasis `faded slate`
//     control: `bg-n-slate-9/10` under `text-n-slate-12`. Both tokens sit near the page's
//     own background, so halving the element's opacity moves them TOGETHER and the label
//     stays legible (~4:1 measured, against ~2:1) instead of dissolving into its own fill.
//
// `disabled:!opacity-75` is the second half, and it is the half that carries the number.
// `faded slate` alone measured 3.07:1 in light — better than 2:1 and still not a figure
// worth defending to the next critic, because `opacity-50` throws away half of whatever
// contrast the tokens provide before anyone gets to look at it. At 75% the same pair
// measures 6.6:1 light / 8.7:1 dark and still reads unmistakably inactive, because the
// inactive signal is now carried by EMPHASIS (a flat slate fill next to no accent) rather
// than by illegibility. The `!` is not decoration: `disabled:opacity-50` lives in
// `Button.vue`'s base string at identical specificity, so without it the winner is
// stylesheet order — which is Tailwind's business, not ours, and would silently flip on an
// upstream bump.
//
// The button is deliberately still RENDERED when idle. Deleting it was the other candidate
// and it re-opens the finding this bar was built to close — "there is no save button, save
// state, or 'saved' confirmation anywhere in view" — for the single state the page spends
// most of its life in, the one it loads in. The commit point must not appear and disappear;
// only its emphasis may.
const isActionable = computed(() =>
  ['dirty', 'saving', 'error'].includes(props.state)
);

// On is the accent, off is a plain slate pill. Never ruby: an off task is not an error,
// it is the normal state of something not finished yet, and colouring it as a fault is how
// a product teaches an owner to ignore its red.
const noteClass = computed(() =>
  props.noteTone === 'on'
    ? 'bg-n-blue-3 text-n-blue-11'
    : 'bg-n-alpha-2 text-n-slate-11'
);
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-between py-4 gap-x-6 gap-y-3"
    data-waslo-save-bar
    :data-state="state"
  >
    <div class="flex flex-wrap items-start min-w-0 gap-x-3 gap-y-2">
      <div class="flex items-start min-w-0 gap-2">
        <Spinner v-if="state === 'saving'" class="shrink-0 mt-0.5 size-4" />
        <span v-else class="shrink-0 mt-0.5 size-4" :class="iconClass" />
        <div class="flex flex-col min-w-0">
          <span
            class="text-sm font-medium"
            :class="statusClass"
            data-waslo-save-status
          >
            {{ status }}
          </span>
          <span
            v-if="detail"
            class="text-sm text-n-slate-11"
            data-waslo-save-detail
          >
            {{ detail }}
          </span>
        </div>
      </div>
      <!-- The second fact. On screen in every state, including the ones whose detail line
           is about something else entirely. -->
      <span
        v-if="note"
        class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-full shrink-0"
        :class="noteClass"
        data-waslo-save-note
        :data-tone="noteTone"
      >
        {{ note }}
      </span>
    </div>

    <!--
      The button exists only when there is something to press. Two rounds tried to make a
      DISABLED primary legible — 'faded'/slate, then `disabled:!opacity-75` — and blind
      critics in both themes still called it "the lowest-contrast element on either screen"
      and "half-styled". They also named the real problem, which was never contrast: a bar
      reading "Everything is saved" beside a Save button is "two contradictory signals in
      one bar".

      So there is no third contrast attempt. When nothing is unsaved the status text IS the
      whole answer, and the bar holds one message instead of a message and a dead control.
      The button returns, at full solid strength, the moment an edit makes it meaningful.
    -->
    <Button
      v-if="isActionable || state === 'saving'"
      :label="saveLabel"
      variant="solid"
      color="blue"
      :is-loading="state === 'saving'"
      :disabled="disabled"
      data-waslo-save-button
      @click="emit('save')"
    />
  </div>
</template>
